import React, { useState, useEffect } from 'react'
import pdfMake from '../../../utils/pdfMakeInit'
import { useDispatch } from 'react-redux'
import { format } from 'date-fns'
import Button from '@mui/material/Button'
import Tooltip from '@material-ui/core/Tooltip'
import { useNavigate, useParams } from 'react-router-dom'
import Typography from '@mui/material/Typography'
import { alpha, useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import { toast } from 'react-toastify'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Avatar from '@mui/material/Avatar'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import TextField from '@mui/material/TextField'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormControl from '@mui/material/FormControl'
import Select from '@mui/material/Select'
import InputLabel from '@mui/material/InputLabel'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import ListItemText from '@mui/material/ListItemText'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import DownloadIcon from '@mui/icons-material/Download'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import PersonIcon from '@mui/icons-material/Person'
import PhoneIcon from '@mui/icons-material/Phone'
import EmailIcon from '@mui/icons-material/Email'
import HomeIcon from '@mui/icons-material/Home'
import BusinessIcon from '@mui/icons-material/Business'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import SpeedIcon from '@mui/icons-material/Speed'
import TimerIcon from '@mui/icons-material/Timer'
import BadgeIcon from '@mui/icons-material/Badge'
import BloodtypeIcon from '@mui/icons-material/Bloodtype'
import CloseIcon from '@mui/icons-material/Close'
import DescriptionIcon from '@mui/icons-material/Description'
import GroupsIcon from '@mui/icons-material/Groups'
import AppBar from '@mui/material/AppBar'
import Container from '../../GeneralComponents/Container'
import { Topbar, Sidebar, Table } from '../components'
import setGlobalLoader from '../../../redux/actions/loaderActions'
import {
  getStudentById,
  getAttendanceByStudentId,
  deleteAttendance,
  getInvoice,
  updateInvoicePayment,
  getBatches,
  assignStudentToBatch,
  removeStudentFromBatch,
} from '../../../redux/actions/apiActions'
import { certificateImage } from '../../../utils/certificateBase64'
import { urlToBase64 } from '../../../utils/urlToBase64'
import { buildReportPDF } from '../../../utils/reportTemplate'


const StatCard = ({ icon, title, value, color }) => {
  return (
    <Card
      sx={{
        height: '100%',
        background: `linear-gradient(135deg, ${alpha(color, 0.1)} 0%, ${alpha(color, 0.05)} 100%)`,
        border: `1px solid ${alpha(color, 0.2)}`,
        borderRadius: 3,
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box display="flex" alignItems="center" gap={2}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(color, 0.15),
            }}
          >
            {icon}
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              {title}
            </Typography>
            <Typography variant="h5" fontWeight={700} color={color}>
              {value}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  )
}

const InfoRow = ({ icon, label, value }) => {
  const theme = useTheme()
  return (
    <Box
      display="flex"
      alignItems="center"
      gap={2}
      py={1.5}
      sx={{
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
        '&:last-child': { borderBottom: 'none' }
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: alpha(theme.palette.primary.main, 0.1),
        }}
      >
        {icon}
      </Box>
      <Box flex={1}>
        <Typography variant="caption" color="text.secondary" display="block">
          {label}
        </Typography>
        <Typography variant="body2" fontWeight={600}>
          {value || '-'}
        </Typography>
      </Box>
    </Box>
  )
}

const TableComponent = ({ columns, data, title }) => {
  const theme = useTheme()

  return (
    <Box>
      <Box
        width={1}
        height={1}
        borderRadius={2}
        border={`1px solid ${theme.palette.divider}`}
        overflow="hidden"
      >
        <Table
          columns={columns}
          data={data}
          export={false}
          selection={false}
          toolbar
          title={title}
        />
      </Box>
    </Box>
  )
}

const ViewStudent = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [studentDetails, setStudentDetails] = useState({})
  const [attendanceList, setAttendanceList] = useState([])
  const [totalKmDriven, setTotalKmDriven] = useState(0)
  const [totalMinutesDriven, setTotalMinutesDriven] = useState(0)
  const [invoice, setInvoice] = useState(null)
  const [editPaymentDialog, setEditPaymentDialog] = useState(false)
  const [editPaymentRow, setEditPaymentRow] = useState(null)
  const [editPaymentAmount, setEditPaymentAmount] = useState('')
  const [editPaymentMode, setEditPaymentMode] = useState('CASH')
  const [editPaymentSaving, setEditPaymentSaving] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [previewImage, setPreviewImage] = useState('')
  const [downloadAnchorEl, setDownloadAnchorEl] = useState(null)
  const [certPreviewOpen, setCertPreviewOpen] = useState(false)
  const [certDataUrl, setCertDataUrl] = useState('')
  const [batchDialog, setBatchDialog] = useState(false)
  const [batchOptions, setBatchOptions] = useState([])
  const [selectedBatch, setSelectedBatch] = useState('')
  const [batchSaving, setBatchSaving] = useState(false)
  const theme = useTheme()
  const isMd = useMediaQuery(theme.breakpoints.up('md'), {
    defaultMatches: true,
  })

  const params = useParams()

  const [openSidebar, setOpenSidebar] = useState(false)

  const handleSidebarOpen = () => {
    setOpenSidebar(true)
  }

  const handleSidebarClose = () => {
    setOpenSidebar(false)
  }
  const open = isMd ? false : openSidebar

  const handlePreviewClick = (imageUrl) => {
    setPreviewImage(imageUrl)
    setPreviewOpen(true)
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'COMPLETED':
        return 'success'
      case 'INPROGRESS':
        return 'warning'
      case 'ONBOARDED':
        return 'info'
      default:
        return 'default'
    }
  }

  const handleDeleteAttendance = async rowData => {
    let text = `Are you sure you want to delete this attendance record ?`
    if (window.confirm(text) === true) {
      dispatch(setGlobalLoader(true))
      try {
        await deleteAttendance(rowData._id)
        setAttendanceList(attendanceList.filter(d => d._id !== rowData._id))
        dispatch(setGlobalLoader(false))
      } catch (error) {
        dispatch(setGlobalLoader(false))
        console.log('delete attendance error', error)
        toast.error(error.response.data.msg)
      }
    } else {
      toast.success('Request Cancelled')
    }
  }

  const handleOpenBatchDialog = async () => {
    try {
      const res = await getBatches('?limit=10000')
      setBatchOptions(res?.data?.data?.docs || [])
      const currentId = studentDetails?.batchId?._id || studentDetails?.batchId || ''
      setSelectedBatch(currentId ? String(currentId) : '')
    } catch (err) {
      toast.error(err?.response?.data?.message ?? 'Error loading batches')
    }
    setBatchDialog(true)
  }

  const handleAssignBatch = async () => {
    if (!selectedBatch) return
    setBatchSaving(true)
    try {
      await assignStudentToBatch(selectedBatch, params.id)
      const matched = batchOptions.find((b) => b._id === selectedBatch)
      toast.success('Student assigned to batch')
      setStudentDetails((prev) => ({ ...prev, batchId: { _id: selectedBatch, name: matched?.name || '' } }))
      setBatchDialog(false)
    } catch (err) {
      toast.error(err?.response?.data?.message ?? 'Error assigning batch')
    } finally {
      setBatchSaving(false)
    }
  }

  const handleRemoveBatch = async () => {
    const batchId = studentDetails?.batchId?._id || studentDetails?.batchId
    if (!batchId) return
    if (!window.confirm('Remove student from current batch?')) return
    dispatch(setGlobalLoader(true))
    try {
      await removeStudentFromBatch(batchId, params.id)
      toast.success('Student removed from batch')
      setStudentDetails((prev) => ({ ...prev, batchId: null }))
    } catch (err) {
      toast.error(err?.response?.data?.message ?? 'Error removing from batch')
    } finally {
      dispatch(setGlobalLoader(false))
    }
  }

  const columns = [
    { title: 'Sr no.', field: 'index', filtering: false },
    { title: 'KM Driven', field: 'kmDriven', filtering: false },
    { title: 'Minutes Driven', field: 'minutesDriven', filtering: false },
    { title: 'Instructor', field: 'driver', filtering: false },
    { title: 'Class Type', field: 'classType', filtering: false },
    { title: 'Date', field: 'date', filtering: false },
    {
      title: 'Actions',
      field: '',
      filtering: false,
      render: rowData => (
        <div className="tableActions ">
          <Tooltip title="Edit">
            <EditIcon
              onClick={() =>
                navigate(`/students/attendance/edit/${rowData._id}`, {
                  state: {
                    studentName: studentDetails?.name,
                    date: format(new Date(rowData?.createdAt), 'MMM dd, yyyy'),
                    kmDriven: rowData.kmDriven,
                    minutesDriven: rowData.minutesDriven,
                  },
                })
              }
            />
          </Tooltip>
          <Tooltip title="Delete">
            <DeleteIcon onClick={() => handleDeleteAttendance(rowData)} />
          </Tooltip>
        </div>
      ),
    },
  ]

  useEffect(() => {
    dispatch(setGlobalLoader(true))
    if (params.id !== null && params.id !== undefined) {
      async function studentFunc() {
        try {
          const query = `?studentId=${params.id}`
          const [studentRes, attendanceRes, invoiceRes] = await Promise.all([
            getStudentById(params.id),
            getAttendanceByStudentId(query),
            getInvoice(query),
          ])
          setStudentDetails(studentRes?.data?.data)
          setInvoice(invoiceRes?.data?.data || null)
          const data = attendanceRes?.data?.data?.docs?.map((d, i) => ({
            index: i + 1,
            ...d,
            date: format(new Date(d?.createdAt), 'MMM dd, yyyy hh:mm a'),
            driver: d?.driverId?.name,
            classType: d?.classType || '-',
          }))
          setAttendanceList(data)
          const kmDriven = data
            .map(d => d.kmDriven || 0)
            .reduce(
              (accumulator, currentValue) => accumulator + currentValue,
              0
            )
          setTotalKmDriven(kmDriven)
          const minutesDriven = data
            .map(d => d.minutesDriven || 0)
            .reduce(
              (accumulator, currentValue) => accumulator + currentValue,
              0
            )
          setTotalMinutesDriven(minutesDriven)
          dispatch(setGlobalLoader(false))
        } catch (error) {
          dispatch(setGlobalLoader(false))
          console.log('get student details and attendance error', error)
          toast.error(
            error.response.data.message ??
            'some error occured while fetching student and attendance details.'
          )
        }
      }
      studentFunc()
    }
  }, [params.id])

  const capitalizeString = values => {
    const name = values.toLowerCase().split(' ')
    const capitalizeNameArray = name.map(
      n => n.charAt(0).toUpperCase() + n.substr(1)
    )
    return capitalizeNameArray.join(' ')
  }
  const checkNameLength = name => {
    if (name.length <= 18) {
      return name
    } else {
      const nameArr = name.split(' ')
      if (nameArr[0].length >= 15) {
        return nameArr[0].substr(0, 15) + '...'
      }

      const firstName = nameArr[0]
      const lastNameInital = nameArr[nameArr.length - 1][0]
      const fullName = firstName + ' ' + lastNameInital + '.'
      return fullName
    }
  }

  const buildDocDefinition = (logoBase64 = null) => {
    const enrollmentDate = new Date(studentDetails?.createdAt).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).replace(/\//g, '/')

    const currentDate = new Date().toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).replace(/\//g, '/')

    return {
      pageSize: 'A5',
      pageOrientation: 'landscape',
      pageMargins: [0, 0, 0, 0],
      info: {
        title: 'Drivolo - Completion Certificate',
      },
      background: {
        image: certificateImage,
        width: '595',
      },
      content: [
        {
          text: (studentDetails?.organizationId?.certificateName || studentDetails?.organizationId?.name || '').toUpperCase(),
          style: 'header',
        },
        {
          text: `${(
            studentDetails?.organizationId?.city ?? ""
          ).toUpperCase()} , ${(studentDetails?.organizationId?.state ?? "").toUpperCase()} , ${(studentDetails?.organizationId?.pincode.toString() ?? "").toUpperCase()} `,
          style: 'sub_header',
        },
        {
          columns: [
            {
              width: '15%',
              stack: []
            },
            {
              width: '40%',
              stack: [
                {
                  text: !!studentDetails?.organizationId?.license ? `License No - ${studentDetails?.organizationId.license.toString().toUpperCase()}` : '',
                  style: 'license',
                }
              ]
            },
            // {
            //   width: '33%',
            //   stack: [
            //     {
            //       text: !!studentDetails?.organizationId?.displayId ? `MDS Code - ${studentDetails?.organizationId.displayId.toString().toUpperCase()}` : '',
            //       style: 'mdsCode',
            //     },
            //   ]
            // },
            {
              width: '30%',
              stack: [
                {
                  text: !!studentDetails?.displayId ? `Roll No - ${studentDetails?.displayId.toString().toUpperCase()}` : '',
                  style: 'rollNo',
                },
              ]
            },
            {
              width: '15%',
              stack: []
            },
          ],
        },
        {
          text: checkNameLength(capitalizeString(studentDetails?.name)),
          style: 'studentName',
        },

        {
          text: `${enrollmentDate}`,
          style: 'bodyText',
        },
        {
          columns: [
            {
              width: '*',
              stack: [
                {
                  text: currentDate,
                  style: 'dateValue',
                }
              ]
            },
            {
              width: 90,
              alignment: 'center',
              stack: logoBase64
                ? [{ image: logoBase64, width: 80, height: 80, alignment: 'center', margin: [0, 25, 0, 0] }]
                : [],
            },
            {
              width: '*',
              stack: [
                {
                  text: checkNameLength(capitalizeString(studentDetails?.organizationId?.proprietorName || attendanceList[0]?.driver || " ")),
                  style: 'instructorName',
                },
              ]
            }
          ],
        }
      ],
      styles: {
        header: {
          fontSize: 18,
          font: 'Belleza',
          alignment: 'center',
          margin: [0, 40, 0, 0],
        },
        sub_header: {
          fontSize: 14,
          alignment: 'center',
          margin: [0, 3, 0, 5],
          color: '#666666',
          characterSpacing: 0.5,
        },
        license: {
          fontSize: 11,
          alignment: 'center',
          margin: [0, 10, 0, 0],
          color: '#666666',
          characterSpacing: 0.5,
        },
        // mdsCode: {
        //   fontSize: 11,
        //   alignment: 'center',
        //   margin: [0, 0, 0, 0],
        //   color: '#666666',
        //   characterSpacing: 0.5,
        // },
        rollNo: {
          fontSize: 11,
          alignment: 'center',
          margin: [0, 10, 0, 0],
          color: '#666666',
          characterSpacing: 0.5,
        },
        studentName: {
          fontSize: 36,
          font: 'AlexBrush',
          alignment: 'center',
          margin: [0, 102, 0, 0],
        },
        bodyText: {
          fontSize: 12,
          bold: true,
          // alignment: 'center',
          lineHeight: 1.3,
          margin: [185, 0, 0, 0],
        },
        dateValue: {
          fontSize: 12,
          bold: true,
          margin: [90, 58, 0, 0],
        },
        instructorName: {
          fontSize: 12,
          bold: true,
          alignment: 'center',
          margin: [0, 59, 0, 0],
        },
      },
    }
  }

  const handleEditPaymentOpen = (payment) => {
    setEditPaymentRow(payment)
    setEditPaymentAmount(String(payment.amountReceived))
    setEditPaymentMode(payment.paymentMode)
    setEditPaymentDialog(true)
  }

  const handleEditPaymentSave = async () => {
    if (!editPaymentRow || !invoice) return
    const amount = Number(editPaymentAmount)
    if (isNaN(amount) || amount <= 0) { toast.error('Enter a valid amount.'); return }
    setEditPaymentSaving(true)
    try {
      const res = await updateInvoicePayment(invoice._id, editPaymentRow._id, {
        amountReceived: amount,
        paymentMode: editPaymentMode,
      })
      setInvoice(res?.data?.data)
      toast.success('Payment updated.')
      setEditPaymentDialog(false)
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Error updating payment.')
    } finally {
      setEditPaymentSaving(false)
    }
  }

  const fetchLogoBase64 = async (logoUrl) => {
    if (!logoUrl) return null
    try {
      return await urlToBase64(logoUrl)
    } catch {
      return null
    }
  }

  const generateCertificate = async () => {
    const logoBase64 = await fetchLogoBase64(studentDetails?.organizationId?.logo)
    pdfMake
      .createPdf(buildDocDefinition(logoBase64))
      .download(`${studentDetails.name}-${new Date().toLocaleDateString()}`)
  }

  const previewCertificate = async () => {
    const logoBase64 = await fetchLogoBase64(studentDetails?.organizationId?.logo)
    pdfMake.createPdf(buildDocDefinition(logoBase64)).getDataUrl((dataUrl) => {
      setCertDataUrl(dataUrl)
      setCertPreviewOpen(true)
    })
  }

  const generateReport = async () => {
    dispatch(setGlobalLoader(true))
    try {
      const orgLogoBase64 = await fetchLogoBase64(studentDetails?.organizationId?.logo)
      const doc = buildReportPDF(studentDetails, attendanceList, orgLogoBase64)
      const today = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' })
      pdfMake.createPdf(doc).download(`Report-${studentDetails.name}-${today}.pdf`)
    } catch (err) {
      toast.error('Error generating report.')
    } finally {
      dispatch(setGlobalLoader(false))
    }
  }

  return (
    <Box>
      <AppBar
        position={'fixed'}
        sx={{
          backgroundColor: theme.palette.background.paper,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
        elevation={0}
      >
        <Container maxWidth={1} paddingY={{ xs: 1, sm: 1.5 }}>
          <Topbar onSidebarOpen={handleSidebarOpen} />
        </Container>
      </AppBar>
      <Sidebar
        onClose={handleSidebarClose}
        open={open}
        variant={isMd ? 'permanent' : 'temporary'}
      />
      <main>
        <Box height={{ xs: 94, sm: 102, md: 107 }} />
        <Box
          display="flex"
          flex="1 1 auto"
          overflow="hidden"
          paddingLeft={{ md: '256px' }}
        >
          <Box display="flex" flex="1 1 auto" overflow="hidden">
            <Box flex="1 1 auto" height="100%" overflow="auto" p={{ xs: 2, md: 4 }}>

              {/* Header Section */}
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                flexDirection={{ xs: 'column', sm: 'row' }}
                gap={2}
                mb={4}
              >
                <Typography variant="h5" fontWeight={700}>
                  Student Details
                </Typography>
                <Box display="flex" gap={2} flexWrap="wrap">
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<EditIcon />}
                    onClick={() => navigate(`/students/edit/${params.id}`)}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<GroupsIcon />}
                    onClick={handleOpenBatchDialog}
                  >
                    {studentDetails?.batchId ? 'Change Batch' : 'Assign Batch'}
                  </Button>
                  {studentDetails?.batchId && (
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={handleRemoveBatch}
                    >
                      Remove from Batch
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<DownloadIcon />}
                    endIcon={<ArrowDropDownIcon />}
                    onClick={(e) => setDownloadAnchorEl(e.currentTarget)}
                  >
                    Download
                  </Button>
                  <Menu
                    anchorEl={downloadAnchorEl}
                    open={Boolean(downloadAnchorEl)}
                    onClose={() => setDownloadAnchorEl(null)}
                  >
                    <MenuItem
                      onClick={() => {
                        setDownloadAnchorEl(null)
                        previewCertificate()
                      }}
                    >
                      <ListItemText>Preview Certificate</ListItemText>
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setDownloadAnchorEl(null)
                        generateCertificate()
                      }}
                    >
                      <ListItemText>Download Certificate</ListItemText>
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setDownloadAnchorEl(null)
                        generateReport()
                      }}
                    >
                      <ListItemText>Download Report</ListItemText>
                    </MenuItem>
                  </Menu>
                </Box>
              </Box>

              {/* Profile Card */}
              <Card sx={{ mb: 4, borderRadius: 3, overflow: 'visible' }}>
                <CardContent sx={{ p: { xs: 2, md: 4 } }}>
                  <Grid container spacing={3}>
                    {/* Profile Image & Basic Info */}
                    <Grid item xs={12} md={4}>
                      <Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
                        <Avatar
                          src={studentDetails?.imageUrl}
                          sx={{
                            width: 120,
                            height: 120,
                            mb: 2,
                            border: `4px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                            cursor: studentDetails?.imageUrl ? 'pointer' : 'default',
                          }}
                          onClick={() => studentDetails?.imageUrl && handlePreviewClick(studentDetails.imageUrl)}
                        >
                          <PersonIcon sx={{ fontSize: 60 }} />
                        </Avatar>
                        <Typography variant="h6" fontWeight={700} gutterBottom>
                          {studentDetails?.name}
                        </Typography>
                        <Chip
                          label={studentDetails?.status}
                          color={getStatusColor(studentDetails?.status)}
                          size="small"
                          sx={{ fontWeight: 600 }}
                        />
                        {studentDetails?.organizationId?.name && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            {studentDetails?.organizationId?.name}
                          </Typography>
                        )}
                        {studentDetails?.batchId && (
                          <Chip
                            icon={<GroupsIcon />}
                            label={studentDetails.batchId?.name || 'Batch'}
                            size="small"
                            color="primary"
                            sx={{ mt: 1 }}
                          />
                        )}
                      </Box>
                    </Grid>

                    {/* Stats Cards */}
                    <Grid item xs={12} md={8}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <StatCard
                            icon={<SpeedIcon sx={{ color: theme.palette.primary.main, fontSize: 28 }} />}
                            title="Total KM Driven"
                            value={`${totalKmDriven} KM`}
                            color={theme.palette.primary.main}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <StatCard
                            icon={<TimerIcon sx={{ color: theme.palette.success.main, fontSize: 28 }} />}
                            title="Total Minutes Driven"
                            value={`${totalMinutesDriven} Min`}
                            color={theme.palette.success.main}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <StatCard
                            icon={<CalendarTodayIcon sx={{ color: theme.palette.info.main, fontSize: 28 }} />}
                            title="Enrolled On"
                            value={studentDetails?.createdAt ? format(new Date(studentDetails.createdAt), 'MMM dd, yyyy') : '-'}
                            color={theme.palette.info.main}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <StatCard
                            icon={<DescriptionIcon sx={{ color: theme.palette.warning.main, fontSize: 28 }} />}
                            title="Total Sessions"
                            value={attendanceList?.length || 0}
                            color={theme.palette.warning.main}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Payment Summary Card */}
              {invoice && (
                <Card sx={{ mb: 4, borderRadius: 3 }}>
                  <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                    <Typography variant="subtitle1" fontWeight={700} color="primary" mb={2}>
                      Payment Summary
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <StatCard
                          icon={<DescriptionIcon sx={{ color: theme.palette.info.main, fontSize: 28 }} />}
                          title="Total Fee Agreed"
                          value={`₹${(invoice.totalAmount || 0).toLocaleString('en-IN')}`}
                          color={theme.palette.info.main}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <StatCard
                          icon={<DescriptionIcon sx={{ color: theme.palette.success.main, fontSize: 28 }} />}
                          title="Total Paid"
                          value={`₹${(invoice.totalPaid || 0).toLocaleString('en-IN')}`}
                          color={theme.palette.success.main}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <StatCard
                          icon={<DescriptionIcon sx={{ color: invoice.balanceDue > 0 ? theme.palette.error.main : theme.palette.success.main, fontSize: 28 }} />}
                          title="Balance Due"
                          value={`₹${(invoice.balanceDue || 0).toLocaleString('en-IN')}`}
                          color={invoice.balanceDue > 0 ? theme.palette.error.main : theme.palette.success.main}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              )}

              {/* Details Grid */}
              <Grid container spacing={3} mb={4}>
                {/* Contact Information */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ height: '100%', borderRadius: 3 }}>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="subtitle1" fontWeight={700} color="primary" mb={2}>
                        Contact Information
                      </Typography>
                      <InfoRow
                        icon={<PhoneIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />}
                        label="Phone Number"
                        value={studentDetails?.phoneNumber}
                      />
                      <InfoRow
                        icon={<EmailIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />}
                        label="Email"
                        value={studentDetails?.email}
                      />
                      <InfoRow
                        icon={<PersonIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />}
                        label="Guardian Name"
                        value={studentDetails?.guardianName}
                      />
                      <InfoRow
                        icon={<PhoneIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />}
                        label="Guardian Phone"
                        value={studentDetails?.guardianPhoneNumber}
                      />
                    </CardContent>
                  </Card>
                </Grid>

                {/* Personal Information */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ height: '100%', borderRadius: 3 }}>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="subtitle1" fontWeight={700} color="primary" mb={2}>
                        Personal Information
                      </Typography>
                      <InfoRow
                        icon={<CalendarTodayIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />}
                        label="Date of Birth"
                        value={studentDetails?.dob ? format(new Date(studentDetails.dob), 'MMM dd, yyyy') : null}
                      />
                      <InfoRow
                        icon={<BloodtypeIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />}
                        label="Blood Group"
                        value={studentDetails?.bloodGroup}
                      />
                      <InfoRow
                        icon={<BadgeIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />}
                        label="Roll No."
                        value={studentDetails?.displayId}
                      />
                      <InfoRow
                        icon={<BadgeIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />}
                        label="Learning License No."
                        value={studentDetails?.learningLicenseNumber}
                      />
                      <InfoRow
                        icon={<BusinessIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />}
                        label="Organization"
                        value={studentDetails?.organizationId?.name}
                      />
                    </CardContent>
                  </Card>
                </Grid>

                {/* Address Information */}
                <Grid item xs={12}>
                  <Card sx={{ borderRadius: 3 }}>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="subtitle1" fontWeight={700} color="primary" mb={2}>
                        Address
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={3}>
                          <InfoRow
                            icon={<HomeIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />}
                            label="Address"
                            value={studentDetails?.address}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <InfoRow
                            icon={<HomeIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />}
                            label="City"
                            value={studentDetails?.city}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <InfoRow
                            icon={<HomeIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />}
                            label="State"
                            value={studentDetails?.state}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <InfoRow
                            icon={<HomeIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />}
                            label="Pincode"
                            value={studentDetails?.pincode}
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Documents Section */}
                {(studentDetails?.learningLicenseImg || studentDetails?.addressProofImg || (studentDetails?.documents && studentDetails.documents.length > 0)) && (
                  <Grid item xs={12}>
                    <Card sx={{ borderRadius: 3 }}>
                      <CardContent sx={{ p: 3 }}>
                        <Typography variant="subtitle1" fontWeight={700} color="primary" mb={2}>
                          Documents
                        </Typography>
                        <Box display="flex" flexWrap="wrap" gap={2}>
                          {studentDetails?.learningLicenseImg && (
                            <Box
                              sx={{
                                position: 'relative',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                              }}
                            >
                              <Box
                                component="img"
                                src={studentDetails.learningLicenseImg}
                                alt="Learning License"
                                onClick={() => handlePreviewClick(studentDetails.learningLicenseImg)}
                                sx={{
                                  width: 120,
                                  height: 90,
                                  objectFit: 'cover',
                                  borderRadius: 2,
                                  cursor: 'pointer',
                                  border: `2px solid ${theme.palette.divider}`,
                                  transition: 'transform 0.2s',
                                  '&:hover': {
                                    transform: 'scale(1.05)',
                                  },
                                }}
                              />
                              <Typography variant="caption" sx={{ mt: 1, fontWeight: 600 }}>
                                Learning License
                              </Typography>
                            </Box>
                          )}
                          {studentDetails?.addressProofImg && (
                            <Box
                              sx={{
                                position: 'relative',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                              }}
                            >
                              <Box
                                component="img"
                                src={studentDetails.addressProofImg}
                                alt="Address Proof"
                                onClick={() => handlePreviewClick(studentDetails.addressProofImg)}
                                sx={{
                                  width: 120,
                                  height: 90,
                                  objectFit: 'cover',
                                  borderRadius: 2,
                                  cursor: 'pointer',
                                  border: `2px solid ${theme.palette.divider}`,
                                  transition: 'transform 0.2s',
                                  '&:hover': {
                                    transform: 'scale(1.05)',
                                  },
                                }}
                              />
                              <Typography variant="caption" sx={{ mt: 1, fontWeight: 600 }}>
                                Address Proof
                              </Typography>
                            </Box>
                          )}
                          {studentDetails?.documents?.map((doc, index) => (
                            <Box
                              key={index}
                              sx={{
                                position: 'relative',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                              }}
                            >
                              <Box
                                component="img"
                                src={doc.url}
                                alt={doc.name}
                                onClick={() => handlePreviewClick(doc.url)}
                                sx={{
                                  width: 120,
                                  height: 90,
                                  objectFit: 'cover',
                                  borderRadius: 2,
                                  cursor: 'pointer',
                                  border: `2px solid ${theme.palette.divider}`,
                                  transition: 'transform 0.2s',
                                  '&:hover': {
                                    transform: 'scale(1.05)',
                                  },
                                }}
                              />
                              <Typography variant="caption" sx={{ mt: 1, fontWeight: 600 }}>
                                {doc.name}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>

              {/* Attendance Table */}
              <Box mb={4}>
                <Typography variant="h6" fontWeight={700} mb={2}>
                  Attendance History
                </Typography>
                <TableComponent
                  data={attendanceList}
                  columns={columns}
                  title="Attendance Records"
                />
              </Box>

              {/* Payment History */}
              {invoice?.payments?.length > 0 && (
                <Box>
                  <Typography variant="h6" fontWeight={700} mb={2}>
                    Payment History
                  </Typography>
                  <TableComponent
                    data={(() => {
                      let running = invoice.totalAmount
                      return invoice.payments.map((p, i) => {
                        running -= p.amountReceived
                        return {
                          ...p,
                          index: i + 1,
                          dateDisplay: p.createdAt ? format(new Date(p.createdAt), 'MMM dd, yyyy') : '—',
                          runningBalance: running,
                        }
                      })
                    })()}
                    columns={[
                      { title: 'Sr No.', field: 'index', filtering: false },
                      { title: 'Date', field: 'dateDisplay', filtering: false },
                      { title: 'Amount Paid (₹)', field: 'amountReceived', filtering: false, render: (r) => r.amountReceived?.toLocaleString('en-IN') },
                      {
                        title: 'Balance After (₹)',
                        field: 'runningBalance',
                        filtering: false,
                        render: (r) => (
                          <Typography variant="body2" fontWeight={600} color={r.runningBalance > 0 ? 'error.main' : 'success.main'}>
                            {r.runningBalance?.toLocaleString('en-IN')}
                          </Typography>
                        ),
                      },
                      { title: 'Mode', field: 'paymentMode', filtering: false },
                      {
                        title: 'Actions',
                        filtering: false,
                        render: (r) => (
                          <Tooltip title="Edit Payment">
                            <EditIcon
                              sx={{ fontSize: 18, cursor: 'pointer', color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                              onClick={() => handleEditPaymentOpen(r)}
                            />
                          </Tooltip>
                        ),
                      },
                    ]}
                    title="Payment Records"
                  />
                </Box>
              )}

            </Box>
          </Box>
        </Box>
      </main>

      {/* Assign Batch Dialog */}
      <Dialog open={batchDialog} onClose={() => setBatchDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {studentDetails?.batchId ? 'Change Batch' : 'Assign to Batch'}
        </DialogTitle>
        <DialogContent>
          {studentDetails?.batchId && (
            <Typography variant="body2" color="text.secondary" mb={2}>
              Current batch: <strong>{studentDetails.batchId?.name || '—'}</strong>
            </Typography>
          )}
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Select Batch</InputLabel>
            <Select
              value={selectedBatch}
              label="Select Batch"
              onChange={(e) => setSelectedBatch(e.target.value)}
            >
              {batchOptions.map((b) => (
                <MenuItem key={b._id} value={b._id}>{b.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBatchDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAssignBatch}
            disabled={!selectedBatch || batchSaving}
          >
            {batchSaving ? 'Saving...' : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Payment Dialog */}
      <Dialog open={editPaymentDialog} onClose={() => setEditPaymentDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit Payment</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Editing payment #{invoice?.payments?.findIndex(p => p._id === editPaymentRow?._id) + 1 || ''}
          </Typography>
          <TextField
            label="Amount Received (₹)"
            type="number"
            fullWidth
            value={editPaymentAmount}
            onChange={(e) => setEditPaymentAmount(e.target.value)}
            InputProps={{ inputProps: { min: 1 } }}
            sx={{ mb: 2 }}
            autoFocus
          />
          <Typography variant="subtitle2" fontWeight={700} mb={1}>Payment Mode</Typography>
          <FormControl>
            <RadioGroup row value={editPaymentMode} onChange={(e) => setEditPaymentMode(e.target.value)}>
              <FormControlLabel value="CASH" control={<Radio />} label="Cash" />
              <FormControlLabel value="UPI" control={<Radio />} label="UPI" />
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditPaymentDialog(false)} disabled={editPaymentSaving}>Cancel</Button>
          <Button variant="contained" onClick={handleEditPaymentSave} disabled={editPaymentSaving}>
            {editPaymentSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Certificate Preview Dialog */}
      <Dialog
        open={certPreviewOpen}
        onClose={() => setCertPreviewOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { height: '90vh' } }}
      >
        <DialogContent sx={{ p: 0, position: 'relative', display: 'flex', flexDirection: 'column' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" px={2} py={1} bgcolor="grey.100">
            <Typography variant="subtitle1" fontWeight={600}>Certificate Preview</Typography>
            <Box display="flex" gap={1}>
              <Button size="small" variant="outlined" onClick={() => { previewCertificate() }}>
                Refresh
              </Button>
              <Button size="small" variant="contained" startIcon={<DownloadIcon />} onClick={() => { generateCertificate() }}>
                Download
              </Button>
              <IconButton size="small" onClick={() => setCertPreviewOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
          <Box flex={1} overflow="hidden">
            {certDataUrl && (
              <iframe
                src={certDataUrl}
                title="Certificate Preview"
                style={{ width: '100%', height: '100%', border: 'none' }}
              />
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          <IconButton
            onClick={() => setPreviewOpen(false)}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'rgba(0,0,0,0.5)',
              color: 'white',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
            }}
          >
            <CloseIcon />
          </IconButton>
          <Box
            component="img"
            src={previewImage}
            alt="Preview"
            sx={{
              width: '100%',
              height: 'auto',
              maxHeight: '80vh',
              objectFit: 'contain',
            }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  )
}

export default ViewStudent
